'use client';

import type { ReactNode } from 'react';
import {
  adaptProductCardToCart,
  adaptProductPageToCart,
  extractProductButtonsProps,
} from '../../../cart/cart-utils';
import { useAddToCart, useProductInventory } from '../../../cart/hooks';
import { useMaxQuantity } from '../../../lib/hooks/use-max-quantity';
import { CustomLink } from '../../custom-link';
import type { ProductPageProps } from './client';

interface ProductButtonsRootProps {
  children: ReactNode;
  className?: string;
}

function ProductButtonsRoot({
  children,
  className = 'col-span-12 grid grid-cols-12 gap-4',
}: ProductButtonsRootProps) {
  return <div className={className}>{children}</div>;
}

interface ProductButtonsAllProps {
  product: ProductPageProps;
  backInStockForm: ReactNode | null;
  domain: string;
  addToCartButtonClassName?: string;
  sampleButtonClassName?: string;
  addToCartContainerClassName?: string;
  sampleContainerClassName?: string;
  className?: string;
  addToCartText?: string;
  sampleText?: string;
  wastageMultiplier?: number;
}

function ProductButtonsAll({
  product: fullProduct,
  backInStockForm,
  domain,
  addToCartButtonClassName,
  sampleButtonClassName,
  addToCartContainerClassName,
  sampleContainerClassName,
  className,
  addToCartText = 'Add to Cart',
  sampleText = 'Get a sample',
  wastageMultiplier = 1,
  productSlug,
}: ProductButtonsAllProps & { productSlug?: string }) {
  const { addItem, quantity } = useAddToCart({ domain });

  // Extract product data using the utility
  const { isPreOrder, product, sampleProduct, fileDownloadURL } =
    // @ts-expect-error - TODO: fix this
    extractProductButtonsProps(fullProduct);

  const { isOutOfStock: productIsOutOfStock } = useProductInventory(
    fullProduct.productGid ?? null
  );

  // Get max quantity for main product
  const maxQuantityAvailable = useMaxQuantity({
    productGid: fullProduct.productGid ?? null,
    variantGid: fullProduct.variants?.[0]?.gid ?? null,
    allowBackorder: fullProduct.allowBackorder ?? false,
  });

  // Get max quantity for sample product
  const sampleMaxQuantity = useMaxQuantity({
    productGid: sampleProduct?.productGid ?? null,
    variantGid: sampleProduct?.variantGid ?? null,
    allowBackorder: sampleProduct?.allowBackorder ?? false,
  });

  const isToolsAndResources = product?.category === 'TOOLS & RESOURCES';

  if (isToolsAndResources && fileDownloadURL) {
    return (
      <div className={className}>
        <CustomLink
          href={fileDownloadURL}
          className={addToCartButtonClassName}
          target="_blank"
        >
          Free Download
        </CustomLink>
      </div>
    );
  }

  function handleAddToCart() {
    if (!product) return;

    const cartData = adaptProductPageToCart(product);
    if (!cartData) return;

    const { product: cartProduct, variant: cartVariant } = cartData;

    // For regular products, use the current product slug
    // For samples, we need to link to the parent product (handled in the component that passes productSlug)
    const productUrl = productSlug ? `/products/${productSlug}` : undefined;

    addItem({
      product: cartProduct,
      variant: cartVariant,
      quantity,
      wastageMultiplier,
      maxQuantityAvailable,
      productUrl: product.category === 'SAMPLE' ? productUrl : undefined,
    });
  }

  function handleAddToSample() {
    if (!sampleProduct) return;

    const cartData = adaptProductCardToCart(sampleProduct);
    if (!cartData) return;

    // For samples, link to the parent product instead of the sample itself
    const sampleProductUrl = productSlug
      ? `/products/${productSlug}`
      : undefined;

    addItem({
      product: cartData.product,
      variant: cartData.variant,
      quantity: 1,
      maxQuantityAvailable: sampleMaxQuantity,
      productUrl: sampleProductUrl,
    });
  }

  const productVariantGid = fullProduct.variants?.[0]?.gid;

  return (
    <ProductButtonsRoot className={className}>
      {!productIsOutOfStock && (
        <div
          className={
            addToCartContainerClassName ||
            'col-span-6 col-start-1 max-md:col-span-12'
          }
        >
          <button
            type="button"
            onClick={handleAddToCart}
            className={addToCartButtonClassName}
          >
            <span>{isPreOrder ? 'Pre-order' : addToCartText}</span>
          </button>
        </div>
      )}
      {productIsOutOfStock && productVariantGid && backInStockForm}
      {sampleProduct && (
        <div
          className={
            sampleContainerClassName || 'col-span-6 max-md:col-span-12'
          }
        >
          <button
            type="button"
            onClick={handleAddToSample}
            className={sampleButtonClassName}
          >
            <span>{sampleText}</span>
          </button>
        </div>
      )}
    </ProductButtonsRoot>
  );
}

ProductButtonsRoot.ProductButtons = ProductButtonsAll;

export const ProductButtons = ProductButtonsRoot;
